/**
 * Runtime Environment Resolver
 *
 * Reads environment configuration from window.__ENV_CONFIG__ (injected by
 * env-config.js before the app bundle loads). This enables the "Build Once
 * Deploy Many" pattern — a single build artifact is deployed to all
 * environments with only env-config.js differing per environment.
 *
 * Falls back to null when window.__ENV_CONFIG__ is not available or invalid,
 * allowing the static Vite-mode-based resolver to take over (local dev).
 */

import type { EnvironmentConfig } from "./environments/environment.types";

/** Extend Window to include the runtime config global */
declare global {
  interface Window {
    __ENV_CONFIG__?: Record<string, unknown>;
  }
}

/** Required keys that must be present in runtime config */
const REQUIRED_KEYS: (keyof EnvironmentConfig)[] = [
  "envName",
  "REACT_APP_E_INVOICING_API_ENDPOINT",
  "ALLOWED_HOST_ORIGINS"
];

/** Pattern matching un-replaced Azure Pipeline tokens like #{VAR_NAME}# */
const UNRESOLVED_TOKEN = /^#\{.+\}#$/;

/**
 * Check if a string value is an un-replaced pipeline token.
 */
function isUnresolvedToken(value: unknown): boolean {
  return typeof value === "string" && UNRESOLVED_TOKEN.test(value);
}

/**
 * Attempt to read and validate runtime config from window.__ENV_CONFIG__.
 * Returns a typed EnvironmentConfig or null if unavailable/invalid.
 */
export function getRuntimeEnvironment(): EnvironmentConfig | null {
  const raw = window.__ENV_CONFIG__;

  if (!raw || typeof raw !== "object") {
    return null;
  }

  // Check for un-replaced pipeline tokens
  const unresolvedKeys = Object.entries(raw)
    .filter(([, value]) => isUnresolvedToken(value))
    .map(([key]) => key);

  if (unresolvedKeys.length > 0) {
    console.error(
      `[ENV] env-config.js contains un-replaced pipeline tokens: ${unresolvedKeys.join(", ")}. ` +
        "Falling back to static environment config."
    );
    return null;
  }

  // Validate required keys are present
  const missingKeys = REQUIRED_KEYS.filter(
    (key) => raw[key] === undefined || raw[key] === null
  );

  if (missingKeys.length > 0) {
    console.error(
      `[ENV] env-config.js is missing required keys: ${missingKeys.join(", ")}. ` +
        "Falling back to static environment config."
    );
    return null;
  }

  // Cast to EnvironmentConfig — the raw object matches the shape
  return raw as unknown as EnvironmentConfig;
}
